<template>
  <DocumentFormSection id="location.city" :required="requiredFields.includes('location.city')" :slot-max-width="400">
    <FieldCity :model-value="city" :readonly="true" :options="cities" />
  </DocumentFormSection>

  <DocumentFormSection
    id="location.additional-cities"
    :required="requiredFields.includes('location.additional-cities')"
    class="max-w-[640px]"
  >
    <div v-if="additionalCityNames.length" class="flex flex-wrap gap-2">
      <span
        v-for="cityName in additionalCityNames"
        :key="cityName"
        class="inline-flex items-center rounded-md bg-neutral-10 px-2 py-1 text-sm"
      >
        {{ cityName }}
      </span>
    </div>
    <p v-else class="text-sm text-text-80">{{ $t("common.no-content") }}</p>
  </DocumentFormSection>

  <DocumentFormSection
    id="location.venues"
    :required="requiredFields.includes('location.venues')"
    class="max-w-[640px]"
  >
    <div v-if="venueNames.length" class="flex flex-wrap gap-2">
      <span
        v-for="venueName in venueNames"
        :key="venueName"
        class="inline-flex items-center rounded-md bg-neutral-10 px-2 py-1 text-sm"
      >
        {{ venueName }}
      </span>
    </div>
    <p v-else class="text-sm text-text-80">{{ $t("common.no-content") }}</p>
  </DocumentFormSection>

  <DocumentFormSection id="location.address" :required="requiredFields.includes('location.address')">
    <FieldPlaceSearch
      :multiline="true"
      :model-value="values?.location?.address.direction ?? ''"
      field-id="field-address"
      :placeholder="$t('common.field.address.placeholder')"
      :readonly="true"
    />
    <div v-if="hasCoordinates" class="mt-4" style="height: 240px">
      <FieldMap map-id="field-map" :center="coords" />
    </div>
  </DocumentFormSection>

  <DocumentFormSection id="location.meeting-point" :required="requiredFields.includes('location.meeting-point')">
    <div class="mt-8">
      <NovaTextEditor
        :model-value="values?.meetingPointDetails"
        :placeholder="$t('experience.description.editor.placeholder')"
        :readonly="true"
        data-testid="curation-location-meeting-point"
      />
    </div>
  </DocumentFormSection>
</template>

<script setup lang="ts">
import FieldCity from "@/features/experience-shared/components/FieldCity.vue";
import { RevisionFormProps } from "../types/forms";
import FieldPlaceSearch from "@/features/experience-calendar/components/FieldPlaceSearch.vue";
import FieldMap from "@/features/experience-calendar/components/FieldMap.vue";
import NovaTextEditor from "@/ui-kit/NovaTextEditor/NovaTextEditor.vue";
import { useGeoCitiesMasterdata } from "@/features/masterdata/composables/useGeoCitiesMasterdata";
import { useVenuesMasterdata } from "@/features/masterdata/composables/useVenuesMasterdata";

const props = defineProps<RevisionFormProps>();

const masterdata = useMasterData();
const cities = masterdata.geoCities;
const { getCityByCode, getCityNameByCode } = useGeoCitiesMasterdata();
const { getVenueNameByCode } = useVenuesMasterdata();

const city = computed(() => getCityByCode(props.values?.location?.address.city ?? ""));

const additionalCityNames = computed(() => {
  return (props.values?.additionalCities ?? []).map((code) => getCityNameByCode(code) || code);
});

const venueNames = computed(() => {
  return (props.values?.venues ?? []).map((code) => getVenueNameByCode(code));
});

const coords = computed(() => ({
  lat: Number(props.values?.location?.latitude),
  lng: Number(props.values?.location?.longitude),
}));
const hasCoordinates = computed(() => {
  return coords.value.lat > 0 && coords.value.lng > 0;
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities";
</style>
